import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { connectionIdSelector, jobByIdSelector } from '../../../shared/store/webSocketReducer';
import { useCustomMutation } from '../../../shared/mutations';
import { QueryKeys } from '../../../shared/queries';
import { httpPostFileAndParse } from '../../../utils/fetchService';
import appendJobFormData from '../../../shared/mutations/utils/job/appendJobFormData';
import { globalContextSelector } from '../../../shared/store/selectors';
import {
  successNotification,
  uploadStartedNotification,
} from '../../../shared/notification/store/actions';
import {
  fetchLastImportData,
  fetchLastImportAcceptedKeys,
  fetchLastImportStagedRows,
  fetchInvalidOrgIds,
  fetchLastFileUploadOn,
  fetchUploadJobStatus,
} from '../store/api';
import { JobStatusString } from '../../../shared/enums/job';
import {
  getExplicitBatchIdFromUploadJob,
  getUniqueJobIdFromUploadJob,
  getHdrIdFromUploadJob,
  getHdrIdFromUploadJobResult,
  hasUploadJobImportReference,
  isUploadJobFailed,
  isUploadJobProcessing,
  isUploadJobSuccess,
  mergeUploadJobData,
  formatUploadJobDateTime,
  formatUploadJobFinishedOnForDisplay,
} from '../../uploadStatus/uploadJobUtils';
import { parseGenericAccountDataUploadFile } from '../parseGenericAccountDataUploadFile';
import {
  diffUploadedAccountDataRows,
  diffFailedUploadAccountDataRows,
  normalizeImportCell,
} from '../diffUploadedAccountDataRows';

const UPLOAD_STARTED_MESSAGE = 'File upload started';
const UPLOAD_SUCCESS_MESSAGE = 'Uploaded file successfully';
const UPLOAD_STARTED_TOAST_DURATION_MS = 5000;
const UPLOAD_JOB_POLL_INTERVAL_MS = 5000;

const createInitialUploadJobData = () => ({
  status: JobStatusString.IN_PROGRESS,
});

// Rows the import discarded carry no recorded reason, so the org ids are checked against the same
// view the import selects from. A failure here only costs the specific message, not the diff.
const resolveInvalidOrgIds = async ({ fileRows, taxYear, period }) => {
  const orgIds = [...new Set(fileRows.map(({ orgId }) => normalizeImportCell(orgId)))].filter(
    Boolean,
  );

  if (!orgIds.length || !taxYear || !period) {
    return new Set();
  }

  try {
    const invalidOrgIds = await fetchInvalidOrgIds({ taxYear, period, orgIds });

    return new Set(Array.isArray(invalidOrgIds) ? invalidOrgIds : []);
  } catch {
    return new Set();
  }
};

const createJobId = (globalContext, jobType) =>
  (
    globalContext.apiRouteParamString.replace(/\s+/g, '').replace(/\//g, '') +
    jobType +
    Date.now().toString()
  ).replace(/\W/g, '');

const useGenericAccountDataUploadJob = ({ jobType, taxYear, period }) => {
  const dispatch = useDispatch();
  const [jobId, setJobId] = useState('');
  const [isUploadActive, setIsUploadActive] = useState(false);
  const [polledJobData, setPolledJobData] = useState(null);
  const [lastFileUploadDisplayTime, setLastFileUploadDisplayTime] = useState('');
  const [uploadFinishedDisplayTime, setUploadFinishedDisplayTime] = useState('');
  const [uploadFileBuffer, setUploadFileBuffer] = useState(null);
  const [didAggregateUpload, setDidAggregateUpload] = useState(false);
  const [fileBackedGridRows, setFileBackedGridRows] = useState(null);
  const [isResolvingFileRows, setIsResolvingFileRows] = useState(false);
  const hasShownCompletionToastRef = useRef(false);
  const globalContext = useSelector(globalContextSelector);
  const connectionId = useSelector(connectionIdSelector);
  const jobById = useSelector(state => jobByIdSelector(state, { jobId }));

  const jobData = useCallback(() => mergeUploadJobData(polledJobData, jobById), [
    jobById,
    polledJobData,
  ]);

  useEffect(() => {
    if (!isUploadActive || !jobId) {
      return undefined;
    }

    let isCancelled = false;
    let intervalId;

    const pollJobStatus = async () => {
      if (isCancelled) {
        return;
      }

      try {
        const jobStatus = await fetchUploadJobStatus({ jobId });

        if (isCancelled || !jobStatus) {
          return;
        }

        setPolledJobData(currentPolledJobData =>
          mergeUploadJobData(currentPolledJobData, jobStatus),
        );

        const isTerminalStatus =
          isUploadJobFailed(jobStatus.status) ||
          (isUploadJobSuccess(jobStatus.status) && hasUploadJobImportReference(jobStatus));

        if (isTerminalStatus) {
          clearInterval(intervalId);
        }
      } catch {
        // Keep polling until the upload session ends or the job reaches a terminal state.
      }
    };

    pollJobStatus();
    intervalId = setInterval(pollJobStatus, UPLOAD_JOB_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
    };
  }, [isUploadActive, jobId]);

  useEffect(() => {
    if (globalContext.isReady) {
      setJobId(currentJobId => currentJobId || createJobId(globalContext, jobType));
    }
  }, [globalContext, jobType]);

  const resetJob = useCallback(() => {
    hasShownCompletionToastRef.current = false;
    setIsUploadActive(false);
    setPolledJobData(null);
    setUploadFinishedDisplayTime('');
    setUploadFileBuffer(null);
    setDidAggregateUpload(false);
    setFileBackedGridRows(null);
    setIsResolvingFileRows(false);

    if (globalContext.isReady) {
      setJobId(createJobId(globalContext, jobType));
    }
  }, [globalContext, jobType]);

  const { mutateAsync: startJob, isLoading: isStartingJob } = useCustomMutation({
    resetKey: QueryKeys.Job.Start,
    customMutate: ({ connectionId, stringifiedJsonData, job, files }) =>
      httpPostFileAndParse({
        route: '/api/job/start',
        body: appendJobFormData({ connectionId, stringifiedJsonData, job, files }),
      }),
    errorMessage: 'Failed to start job',
  });

  const currentJobData = jobData();
  const resultHdrId = getHdrIdFromUploadJobResult(currentJobData);
  const hdrId = resultHdrId ?? getHdrIdFromUploadJob(currentJobData);
  const batchId = getExplicitBatchIdFromUploadJob(currentJobData);
  const uniqueJobId = getUniqueJobIdFromUploadJob(currentJobData);
  // Keyed off the job-reported batch id so an unexpected system failure is not mislabelled as a
  // validation failure just because the async job row id is always available.
  const isValidationError = Boolean(
    currentJobData && isUploadJobFailed(currentJobData.status) && batchId,
  );
  // A successful job only reports a batch id when it left rejected rows staged under it, so the
  // pair means some rows imported and some did not.
  const isPartialImport = Boolean(
    currentJobData && isUploadJobSuccess(currentJobData.status) && hdrId && batchId,
  );

  const isJobInProgress = Boolean(
    isStartingJob || (currentJobData && isUploadJobProcessing(currentJobData.status)),
  );

  const isJobFinished = Boolean(
    currentJobData &&
      (isUploadJobSuccess(currentJobData.status) || isUploadJobFailed(currentJobData.status)),
  );

  // A failure-only upload has no hdrId and no job-reported batchId, so the async job row id is the
  // only reference the grid can load rejected rows from.
  const isGridVisible = Boolean(isJobFinished && (hdrId || batchId || uniqueJobId));

  const fetchLastImportDataPage = useCallback(
    ({ offset, sort }) => {
      const escapedSortModel = sort.length ? encodeURIComponent(JSON.stringify(sort)) : null;

      return fetchLastImportData({
        ...(hdrId && { hdrId }),
        ...(batchId && { batchId }),
        ...(uniqueJobId && { uniqueJobId }),
        offset,
        sort: escapedSortModel,
      });
    },
    [hdrId, batchId, uniqueJobId],
  );

  const startUpload = useCallback(
    async uploadData => {
      hasShownCompletionToastRef.current = false;

      try {
        const { file, fileBuffer, fileName, jsonData } = uploadData;

        dispatch(
          uploadStartedNotification(UPLOAD_STARTED_MESSAGE, {
            duration: UPLOAD_STARTED_TOAST_DURATION_MS,
            alertClassName: 'appkit-alert-upload-started',
          }),
        );

        setPolledJobData(createInitialUploadJobData());
        setUploadFinishedDisplayTime('');
        setFileBackedGridRows(null);
        setUploadFileBuffer(fileBuffer || null);
        setDidAggregateUpload(Boolean(jsonData?.shouldAggregateData));
        setIsUploadActive(true);

        const jobResponse = await startJob({
          connectionId,
          job: {
            id: jobId,
            type: jobType,
          },
          stringifiedJsonData: JSON.stringify(jsonData),
          files: [
            {
              file,
              fileName,
            },
          ],
        });

        const activeJobId = jobResponse?.jobId || jobId;

        setJobId(activeJobId);
        setPolledJobData(currentPolledJobData =>
          mergeUploadJobData(currentPolledJobData, {
            ...jobResponse,
            jobId: activeJobId,
            status: jobResponse?.status ?? JobStatusString.IN_PROGRESS,
          }),
        );

        return jobResponse;
      } catch (error) {
        setIsUploadActive(false);
        setPolledJobData(null);
        setUploadFinishedDisplayTime('');
        setUploadFileBuffer(null);
        setDidAggregateUpload(false);
        setFileBackedGridRows(null);
        throw error;
      }
    },
    [connectionId, dispatch, jobId, jobType, startJob],
  );

  const refreshLastFileUploadDisplayTime = useCallback(
    async (excludeHdrId = null) => {
      if (!taxYear || !period) {
        return;
      }

      try {
        const lastFileUpload = await fetchLastFileUploadOn({ taxYear, period, excludeHdrId });
        setLastFileUploadDisplayTime(formatUploadJobDateTime(lastFileUpload?.lastImportOn) || '');
      } catch {
        // Keep the previous display value when the lookup fails.
      }
    },
    [period, taxYear],
  );

  useEffect(() => {
    refreshLastFileUploadDisplayTime();
  }, [refreshLastFileUploadDisplayTime]);

  useEffect(() => {
    if (!isUploadActive || !hdrId || isJobFinished) {
      return;
    }

    refreshLastFileUploadDisplayTime(hdrId);
  }, [hdrId, isJobFinished, isUploadActive, refreshLastFileUploadDisplayTime]);

  useEffect(() => {
    if (!currentJobData || !isJobFinished || hasShownCompletionToastRef.current) {
      return;
    }

    if (isUploadJobSuccess(currentJobData.status) && !hasUploadJobImportReference(currentJobData)) {
      return;
    }

    if (isUploadJobSuccess(currentJobData.status)) {
      // SLT_2477724 LOCKED: completion banner timestamp (see formatUploadJobFinishedOnForDisplay).
      setUploadFinishedDisplayTime(formatUploadJobFinishedOnForDisplay());
      refreshLastFileUploadDisplayTime();

      dispatch(successNotification(UPLOAD_SUCCESS_MESSAGE));
    }

    hasShownCompletionToastRef.current = true;
    setIsUploadActive(false);
  }, [currentJobData, dispatch, isJobFinished, refreshLastFileUploadDisplayTime]);

  const jobStatus = currentJobData?.status;

  useEffect(() => {
    if (!uploadFileBuffer || !isJobFinished) {
      return undefined;
    }

    // A failed import leaves its rows in the staging table, and a partial import keeps its whole
    // batch there too, so those rows pair every spreadsheet line with the outcome the database
    // recorded. Rows an import never staged are absent from every server query, which is why the
    // uploaded file drives the diff either way.
    const shouldDiffStagedRows =
      Boolean(batchId || uniqueJobId) && (isPartialImport || isUploadJobFailed(jobStatus));
    const shouldDiffAcceptedRows =
      Boolean(hdrId) && isUploadJobSuccess(jobStatus) && !shouldDiffStagedRows;

    if (!shouldDiffAcceptedRows && !shouldDiffStagedRows) {
      return undefined;
    }

    let isCancelled = false;

    const resolveFileBackedGridRows = async () => {
      setIsResolvingFileRows(true);

      try {
        const [fileRows, serverRows] = await Promise.all([
          parseGenericAccountDataUploadFile(uploadFileBuffer),
          shouldDiffAcceptedRows
            ? fetchLastImportAcceptedKeys({ hdrId })
            : fetchLastImportStagedRows({ batchId, uniqueJobId }),
        ]);

        if (isCancelled) {
          return;
        }

        const resolvedServerRows = Array.isArray(serverRows) ? serverRows : [];
        const invalidOrgIds = await resolveInvalidOrgIds({ fileRows, taxYear, period });

        if (isCancelled) {
          return;
        }

        setFileBackedGridRows(
          shouldDiffAcceptedRows
            ? diffUploadedAccountDataRows({
                fileRows,
                importedRows: resolvedServerRows,
                invalidOrgIds,
                shouldAggregateData: didAggregateUpload,
              })
            : diffFailedUploadAccountDataRows({
                fileRows,
                stagedRows: resolvedServerRows,
                invalidOrgIds,
                shouldAggregateData: didAggregateUpload,
              }),
        );
      } catch {
        if (!isCancelled) {
          setFileBackedGridRows(null);
        }
      } finally {
        if (!isCancelled) {
          setIsResolvingFileRows(false);
        }
      }
    };

    resolveFileBackedGridRows();

    return () => {
      isCancelled = true;
    };
  }, [
    batchId,
    didAggregateUpload,
    hdrId,
    isJobFinished,
    isPartialImport,
    jobStatus,
    period,
    taxYear,
    uniqueJobId,
    uploadFileBuffer,
  ]);

  return {
    jobId,
    hdrId,
    batchId,
    uniqueJobId,
    isValidationError,
    isPartialImport,
    isGridVisible,
    isJobInProgress,
    isStartingJob,
    isJobFinished,
    isUploadActive,
    startUpload,
    fetchLastImportDataPage,
    jobData,
    resetJob,
    lastFileUploadDisplayTime,
    uploadFinishedDisplayTime,
    fileBackedGridRows,
    isResolvingFileRows,
  };
};

export default useGenericAccountDataUploadJob;
